import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  earned: boolean;
  earnedAt?: string;
}

interface BadgeStats {
  visitedCount: number;
  routeCount: number;
  reviewCount: number;
  completedRouteCount: number;
  partnerRouteCount: number;
}

function calcBadges(stats: BadgeStats): Badge[] {
  return [
    {
      id: "first_step",
      name: "İlk Adım",
      description: "İlk mekanını ziyaret ettin!",
      icon: "ri-footprint-line",
      color: "from-amber-400 to-amber-600",
      earned: stats.visitedCount >= 1,
    },
    {
      id: "venue_hunter",
      name: "Mekan Avcısı",
      description: "10 farklı mekanı ziyaret ettin!",
      icon: "ri-map-pin-2-line",
      color: "from-emerald-400 to-emerald-600",
      earned: stats.visitedCount >= 10,
    },
    {
      id: "bursa_expert",
      name: "Bursa Uzmanı",
      description: "25 mekan ziyaret ettin!",
      icon: "ri-medal-line",
      color: "from-yellow-400 to-yellow-600",
      earned: stats.visitedCount >= 25,
    },
    {
      id: "city_legend",
      name: "Şehir Efsanesi",
      description: "50 mekan ziyaret ettin!",
      icon: "ri-vip-crown-line",
      color: "from-orange-400 to-red-500",
      earned: stats.visitedCount >= 50,
    },
    {
      id: "first_route",
      name: "İlk Rota",
      description: "İlk rotanı oluşturdun!",
      icon: "ri-route-line",
      color: "from-teal-400 to-teal-600",
      earned: stats.routeCount >= 1,
    },
    {
      id: "route_master",
      name: "Rota Ustası",
      description: "5 farklı rota oluşturdun!",
      icon: "ri-trophy-line",
      color: "from-indigo-400 to-indigo-600",
      earned: stats.routeCount >= 5,
    },
    {
      id: "completer",
      name: "Tamamlayıcı",
      description: "Bir rotanın tüm duraklarını bitirdin!",
      icon: "ri-checkbox-circle-line",
      color: "from-green-400 to-green-600",
      earned: stats.completedRouteCount >= 1,
    },
    {
      id: "reviewer",
      name: "Yorumcu",
      description: "İlk yorumunu yazdın!",
      icon: "ri-chat-quote-line",
      color: "from-rose-400 to-rose-600",
      earned: stats.reviewCount >= 1,
    },
    {
      id: "team_player",
      name: "Takım Oyuncusu",
      description: "Bir rotayı birlikte tamamladın!",
      icon: "ri-team-line",
      color: "from-cyan-400 to-cyan-600",
      earned: stats.partnerRouteCount >= 1,
    },
  ];
}

export function useUserBadges(userId: string | undefined) {
  const [badges, setBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const fetch = async () => {
      setLoading(true);
      const [visitedRes, routesRes, reviewsRes, stopsRes, partnerRes] = await Promise.all([
        supabase.from("visited_venues").select("id", { count: "exact", head: true }).eq("user_id", userId),
        supabase.from("user_routes").select("id", { count: "exact", head: true }).eq("user_id", userId),
        supabase.from("reviews").select("id", { count: "exact", head: true }).eq("user_id", userId),
        supabase
          .from("route_stops")
          .select("route_id, completed")
          .in(
            "route_id",
            (await supabase.from("user_routes").select("id").eq("user_id", userId)).data?.map((r: { id: string }) => r.id) ?? [],
          ),
        supabase.from("user_routes").select("id", { count: "exact", head: true }).eq("user_id", userId).not("partner_user_id", "is", null),
      ]);

      // Compute how many routes have ALL stops completed
      const stopsByRoute: Record<string, { total: number; completed: number }> = {};
      (stopsRes.data ?? []).forEach((s: { route_id: string; completed: boolean }) => {
        if (!stopsByRoute[s.route_id]) stopsByRoute[s.route_id] = { total: 0, completed: 0 };
        stopsByRoute[s.route_id].total++;
        if (s.completed) stopsByRoute[s.route_id].completed++;
      });
      const completedRouteCount = Object.values(stopsByRoute).filter(
        (r) => r.total > 0 && r.total === r.completed,
      ).length;

      const stats: BadgeStats = {
        visitedCount: visitedRes.count ?? 0,
        routeCount: routesRes.count ?? 0,
        reviewCount: reviewsRes.count ?? 0,
        completedRouteCount,
        partnerRouteCount: partnerRes.count ?? 0,
      };

      setBadges(calcBadges(stats));
      setLoading(false);
    };
    fetch();
  }, [userId]);

  return { badges, loading, earnedBadges: badges.filter((b) => b.earned) };
}
